<template>
    <div class="container mx-auto px-4 py-6 max-w-7xl">
        <!-- Header Section -->
        <div class="p-6 mb-6">
            <div
                id="title-general"
                class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4"
            >
                <div class="flex items-center gap-4">
                    <router-link
                        to="/app/images"
                        class="rounded-full bg-brand-blue flex justify-center items-center p-3 shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                    >
                        <span class="text-xl">🏠</span>
                    </router-link>
                    <div>
                        <h1 class="text-3xl font-bold">
                            Detalle de imagen
                            <span class="text-theme-primary"> #{{ id }} </span>
                        </h1>
                    </div>
                </div>

                <div class="flex items-center gap-3">
                    <custom-tooltip>
                        <app-button
                            icon
                            border
                            class="shadow-md"
                            @click="copyTo"
                        >
                            📑
                        </app-button>
                        <template #tooltip>
                            <div class="content-tooltip">
                                <span> Copiar en portapapeles </span>
                            </div>
                        </template>
                    </custom-tooltip>
                    <custom-switch
                        v-if="$ability.hasPermission('image:Update')"
                        v-model="enabledUpdate"
                    >
                        <span class="text-theme-inverse">🖊</span>
                    </custom-switch>
                </div>
            </div>
            <p class="text-sm opacity-75">
                Puedes cambiar únicamente el título de la imagen. Este campo
                ayuda al posicionamiento de las imágenes.
            </p>
        </div>

        <div v-if="loadedAllData" id="image-features" class="space-y-6">
            <!-- Metadata Cards -->
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <!-- Created Date Card -->
                <div
                    v-if="id"
                    class="rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow duration-200"
                >
                    <div
                        class="flex items-center gap-2 text-sm mb-2 opacity-75"
                    >
                        <span>📆</span>
                        <span class="font-medium">Fecha de creación</span>
                    </div>
                    <p class="text-lg font-semibold">
                        {{ imageComponent.createdAt | date }}
                    </p>
                </div>

                <div
                    v-if="id"
                    class="rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow duration-200"
                >
                    <div
                        class="flex items-center gap-2 text-sm mb-2 opacity-75"
                    >
                        <span>✏️</span>
                        <span class="font-medium">Última actualización</span>
                    </div>
                    <p class="text-lg font-semibold">
                        {{ imageComponent.updatedAt | date }}
                    </p>
                </div>

                <div
                    v-if="id"
                    class="rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow duration-200"
                >
                    <div
                        class="flex items-center gap-2 text-sm mb-2 opacity-75"
                    >
                        <span>🖼️</span>
                        <span class="font-medium">Tipo de imagen</span>
                    </div>
                    <p class="text-lg font-bold text-theme-primary">
                        {{ imageSizeLabel(imageComponent.size) }}
                    </p>
                </div>

                <div
                    v-if="id"
                    class="rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow duration-200"
                >
                    <div
                        class="flex items-center gap-2 text-sm mb-2 opacity-75"
                    >
                        <span>📤</span>
                        <span class="font-medium">Estado de carga</span>
                    </div>
                    <p
                        v-if="imageComponent.uploaded"
                        class="text-lg font-semibold text-theme-primary flex items-center gap-2"
                    >
                        <span
                            class="inline-block w-2 h-2 bg-brand-blue rounded-full"
                        ></span>
                        Carga con éxito
                    </p>
                    <p
                        v-else
                        class="text-lg font-semibold text-theme-warning flex items-center gap-2"
                    >
                        <span
                            class="inline-block w-2 h-2 bg-theme-warning rounded-full"
                        ></span>
                        Error en la carga
                    </p>
                </div>
            </div>

            <div class="rounded-xl shadow-lg p-6">
                <h2 class="text-xl font-semibold mb-4 flex items-center gap-2">
                    <span>⚙️</span>
                    Configuración de imagen
                </h2>
                <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <custom-input
                            v-model="imageComponent.url"
                            label="Url"
                            class="w-full"
                            disabled
                        />
                    </div>
                    <div>
                        <custom-input
                            v-model="imageComponent.alt"
                            label="Título de la imagen"
                            class="w-full"
                            :disabled="!enabledUpdate"
                        />
                    </div>
                </div>

                <div
                    v-if="enabledUpdate"
                    class="flex flex-wrap gap-3 justify-center mt-6 pt-6 border-t"
                >
                    <app-button class="shadow-md" @click="updateImageComponent">
                        Actualizar imagen
                    </app-button>
                    <app-button
                        v-if="$ability.hasPermission('image:Delete')"
                        danger
                        class="shadow-md"
                        @click="deleteImageComponent"
                    >
                        Eliminar imagen
                    </app-button>
                    <app-button
                        v-if="$ability.hasPermission('image:DeleteDefinitive')"
                        danger
                        class="shadow-md"
                        @click="deleteImageDefinitelyComponent"
                    >
                        Eliminación definitiva
                    </app-button>
                </div>
            </div>

            <div class="rounded-xl shadow-lg p-6">
                <div class="mb-6">
                    <h2 class="text-xl font-semibold flex items-center gap-2">
                        <span>🖼️</span>
                        {{ imageTypeDescription.title }}
                    </h2>
                    <p class="text-sm opacity-75 mt-1">
                        {{ imageTypeDescription.description }}
                    </p>
                </div>

                <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div class="group">
                        <div
                            class="rounded-lg p-4 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                        >
                            <div class="flex items-center justify-between mb-3">
                                <span
                                    class="inline-flex items-center gap-2 px-3 py-1 bg-brand-blue rounded-full text-xs font-semibold text-white"
                                >
                                    <span>📸</span>
                                    <template v-if="isOptimizedImage">
                                        Large (Optimizada)
                                    </template>
                                    <template v-else-if="isTripleImage">
                                        Large - Desktop
                                    </template>
                                    <template v-else> Original </template>
                                </span>
                                <span class="text-xs opacity-75 font-medium">
                                    <template v-if="isCustomImage">
                                        Sin optimizar
                                    </template>
                                    <template v-else-if="isTripleImage">
                                        1400×600
                                    </template>
                                    <template v-else>
                                        Alta resolución
                                    </template>
                                </span>
                            </div>
                            <div
                                class="relative overflow-hidden rounded-lg shadow-inner border"
                            >
                                <img
                                    loading="lazy"
                                    :src="imageComponent.url"
                                    alt="Large"
                                    class="w-full h-auto object-contain max-h-96 transition-transform duration-300 group-hover:scale-105"
                                />
                            </div>
                            <div class="mt-3 text-xs opacity-75 text-center">
                                <template v-if="isOptimizedImage">
                                    Versión Large - Generada automáticamente
                                </template>
                                <template v-else-if="isTripleImage">
                                    Banner para pantallas grandes
                                </template>
                                <template v-else>
                                    Imagen original sin modificaciones
                                </template>
                            </div>
                        </div>
                    </div>

                    <fragment v-if="isOptimizedImage || isTripleImage">
                        <div class="group">
                            <div
                                class="rounded-lg p-4 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                            >
                                <div
                                    class="flex items-center justify-between mb-3"
                                >
                                    <span
                                        class="inline-flex items-center gap-2 px-3 py-1 bg-brand-blue rounded-full text-xs font-semibold text-white"
                                    >
                                        <span>🖼️</span>
                                        <template v-if="isOptimizedImage">
                                            Normal (Optimizada)
                                        </template>
                                        <template v-else>
                                            Normal - Tablet
                                        </template>
                                    </span>
                                    <span
                                        class="text-xs opacity-75 font-medium"
                                    >
                                        <template v-if="isTripleImage">
                                            778×600
                                        </template>
                                        <template v-else>
                                            Resolución media
                                        </template>
                                    </span>
                                </div>
                                <div
                                    class="relative overflow-hidden rounded-lg shadow-inner border"
                                >
                                    <img
                                        loading="lazy"
                                        :src="
                                            imageComponent.url.replace(
                                                'large',
                                                'normal'
                                            )
                                        "
                                        alt="Normal"
                                        class="w-full h-auto object-contain max-h-96 transition-transform duration-300 group-hover:scale-105"
                                    />
                                </div>
                                <div
                                    class="mt-3 text-xs opacity-75 text-center"
                                >
                                    <template v-if="isOptimizedImage">
                                        Versión Normal - Generada
                                        automáticamente
                                    </template>
                                    <template v-else>
                                        Banner para tablets y pantallas medianas
                                    </template>
                                </div>
                            </div>
                        </div>

                        <div class="group">
                            <div
                                class="rounded-lg p-4 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                            >
                                <div
                                    class="flex items-center justify-between mb-3"
                                >
                                    <span
                                        class="inline-flex items-center gap-2 px-3 py-1 bg-brand-blue rounded-full text-xs font-semibold text-white"
                                    >
                                        <span>🔍</span>
                                        <template v-if="isOptimizedImage">
                                            Small (Optimizada)
                                        </template>
                                        <template v-else>
                                            Small - Móvil
                                        </template>
                                    </span>
                                    <span
                                        class="text-xs opacity-75 font-medium"
                                    >
                                        <template v-if="isTripleImage">
                                            480×480
                                        </template>
                                        <template v-else>
                                            Baja resolución
                                        </template>
                                    </span>
                                </div>
                                <div
                                    class="relative overflow-hidden rounded-lg shadow-inner border"
                                >
                                    <img
                                        loading="lazy"
                                        :src="
                                            imageComponent.url.replace(
                                                'large',
                                                'small'
                                            )
                                        "
                                        alt="Small"
                                        class="w-full h-auto object-contain max-h-96 transition-transform duration-300 group-hover:scale-105"
                                    />
                                </div>
                                <div
                                    class="mt-3 text-xs opacity-75 text-center"
                                >
                                    <template v-if="isOptimizedImage">
                                        Versión Small - Generada automáticamente
                                    </template>
                                    <template v-else>
                                        Banner para dispositivos móviles
                                    </template>
                                </div>
                            </div>
                        </div>
                    </fragment>
                </div>

                <!-- Info Banner -->
                <div
                    v-if="isOptimizedImage"
                    class="mt-6 rounded-lg p-4 border border-theme-border bg-theme-hover shadow-sm"
                >
                    <div class="flex items-start gap-3">
                        <span class="text-2xl flex-shrink-0">💡</span>
                        <div class="text-sm">
                            <p class="font-semibold mb-1 text-theme-info">
                                Sistema de optimización automática
                            </p>
                            <p class="opacity-75 text-theme-muted">
                                El sistema genera automáticamente 3 versiones de
                                cada imagen (Large, Normal, Small) para
                                optimizar los tiempos de carga según el contexto
                                de uso en el ecommerce.
                            </p>
                        </div>
                    </div>
                </div>
                <div
                    v-if="isTripleImage"
                    class="mt-6 rounded-lg p-4 border border-theme-border bg-theme-hover shadow-sm"
                >
                    <div class="flex items-start gap-3">
                        <span class="text-2xl flex-shrink-0">📱</span>
                        <div class="text-sm">
                            <p class="font-semibold mb-1 text-theme-success">
                                Imágenes responsivas manuales
                            </p>
                            <p class="opacity-75 text-theme-muted">
                                Se subieron manualmente 3 imágenes en diferentes
                                tamaños (Large 1400×600, Normal 778×600, Small
                                480×480) para garantizar una experiencia óptima
                                en diferentes dispositivos sin depender de la
                                optimización automática.
                            </p>
                        </div>
                    </div>
                </div>
                <div
                    v-if="isCustomImage"
                    class="mt-6 rounded-lg p-4 border border-purple-300 bg-purple-50 shadow-sm"
                >
                    <div class="flex items-start gap-3">
                        <span class="text-2xl flex-shrink-0">🎨</span>
                        <div class="text-sm">
                            <p class="font-semibold mb-1 text-purple-900">
                                Imagen personalizada sin optimización
                            </p>
                            <p class="opacity-75 text-purple-800">
                                Esta imagen se carga en su resolución original
                                sin ningún tipo de optimización automática.
                                Ideal para casos especiales que requieren
                                control total sobre la calidad y dimensiones de
                                la imagen.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { imageSizeLabel } from "@/common/fields/sizes-images";
import { validateConfirmationMessage } from "@/common/lib/transformers";

import CustomInput from "@/components/utils/CustomInput.vue";

export default {
    name: "ImageDetail",
    components: { CustomInput },
    data: () => ({
        id: 0,
        isLoading: false,
        enabledUpdate: true,
        loadedAllData: false,
        imageComponent: {
            id: 0,
            alt: "",
            url: "",
            size: "",
            createdAt: "2021-10-26T00:27:59.146Z",
            updatedAt: "2021-10-26T00:27:59.146Z",
            uploaded: false
        }
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        isOptimizedImage() {
            return this.imageComponent.size === "squareImage";
        },
        isTripleImage() {
            return this.imageComponent.size === "tripleImage";
        },
        isCustomImage() {
            return this.imageComponent.size === "customImage";
        },
        imageTypeDescription() {
            if (this.isOptimizedImage) {
                return {
                    title: "Imagen cuadrada optimizada",
                    description:
                        "El sistema genera automáticamente 3 versiones optimizadas (Large, Normal, Small) para mejorar los tiempos de carga según el contexto de uso."
                };
            }
            if (this.isTripleImage) {
                return {
                    title: "Imágenes responsivas",
                    description:
                        "Se subieron manualmente 3 imágenes en diferentes tamaños para garantizar una visualización óptima en dispositivos móviles, tablets y escritorio."
                };
            }
            if (this.isCustomImage) {
                return {
                    title: "Imagen personalizada",
                    description:
                        "Esta imagen se carga en su resolución original sin optimización automática. Ideal para casos especiales que requieren control total sobre la calidad."
                };
            }
            return {
                title: "Imagen",
                description: "Visualización de la imagen cargada."
            };
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (!(await verifyPagePermission("image:Get", "/app/images"))) return;
        const id = Number(this.$route.params.id);
        this.id = id;
        await this.getBaseImage(id);
    },
    methods: {
        imageSizeLabel,
        ...mapActions("images", [
            "getImageById",
            "updateImage",
            "deleteImage",
            "deleteImageDefinitively"
        ]),
        async getBaseImage(id) {
            this.isLoading = true;
            this.$loading.show();
            try {
                this.imageComponent = await this.getImageById({ id });
                this.loadedAllData = true;
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app/images");
                }
            }
        },
        async updateImageComponent() {
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateImage(this.imageComponent);
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async deleteImageComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Estas seguro de eliminar la imagen ?",
                icon: "warning",
                confirmButtonColor: "#ee3f59",
                cancelButtonColor: "#253C99",
                confirmButtonText: "Si",
                cancelButtonText: "No",
                showCancelButton: true
            });
            if (dialogResult.isConfirmed) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.deleteImage({
                        id: this.id
                    });
                    this.$router.push("/app/images");
                    this.$loading.hide();
                    this.isLoading = false;
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        async deleteImageDefinitelyComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Estas seguro de eliminar la imagen definitivamente ?",
                icon: "warning",
                text: "Escribe 'Si' para eliminar en S3 también",
                input: "text",
                confirmButtonColor: "#ee3f59",
                cancelButtonColor: "#253C99",
                showCancelButton: true
            });
            if (
                dialogResult.isConfirmed &&
                validateConfirmationMessage(dialogResult.value)
            ) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.deleteImageDefinitively({
                        id: this.id
                    });
                    this.$router.push("/app/images");
                    this.$loading.hide();
                    this.isLoading = false;
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        async copyTo() {
            await navigator.clipboard.writeText(`${this.imageComponent.url}`);
            this.$toast.success({
                title: "Copiado al porta papeles"
            });
        }
    }
};
</script>
